import type { Maybe } from '@adornis/base/utilTypes.js';
import { BuildifyFile, BuildifyImage } from '@adornis/buildify/db/BuildifyFile.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XFilePickerDialog } from '@adornis/dms/client/component/x-file-picker-dialog.js';
import '@adornis/dms/client/component/x-file-upload.js';
import type { FileClassSpec } from '@adornis/dms/client/x-dms.js';
import type { AdornisFile } from '@adornis/file-utils/db/files.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { map, of, switchMap } from 'rxjs';

@customElement('cr-buildify-file-selection')
export class CRBuildifyFileSelection extends FormField<Maybe<string>> {
  @property({ attribute: 'image', type: Boolean }) isImage = new RXController<boolean>(this, false);

  @state() private readonly _fileClassSpec = new RXController(
    this,
    this.isImage.observable.pipe(
      map(isImage => {
        if (isImage)
          return {
            fileClass: BuildifyImage,
            isValid: (file: File) => {
              return file.type.startsWith('image') && !file.type.includes('pdf');
            },
          } as FileClassSpec;
        return {
          fileClass: BuildifyFile,
          isValid: (file: File) => {
            return true;
          },
        } as FileClassSpec;
      }),
    ),
  );
  @state() private readonly _file = new RXController(
    this,
    this.value.observable.pipe(
      switchMap(fileID => {
        const fileClass = this._fileClassSpec.value?.fileClass;
        if (!fileID || !fileClass) return of(null);
        return fileClass.getByID(fileID)(fileClass.allFields);
      }),
    ),
  );

  override render() {
    if (this._file.isLoading) return html` <x-loader></x-loader> `;

    const fileClass = this._fileClassSpec.value;
    if (!fileClass) return html` file class not found `;

    const file = this._file.value as Maybe<AdornisFile>;

    if (file) {
      return html`
        <x-flex space="md">
          <x-text> ${file.meta?.fileName} </x-text>
          <x-button
            icon="upload_file"
            @click=${async () => {
              this._setValue(null);
            }}
          >
            andere Datei auswählen
          </x-button>
        </x-flex>
      `;
    }

    return html`
      <x-flex space="sm">
        <x-file-upload .fileClassSpecs=${[fileClass]}></x-file-upload>
        <x-button
          @click=${async () => {
            const file = (await XFilePickerDialog.selectFile([fileClass])) as Maybe<AdornisFile>;
            if (!file) return;
            this._setValue(file._id);
          }}
        >
          Datei auswählen
        </x-button>
      </x-flex>
    `;
  }

  private _setValue(value: string | null) {
    this.value.next(value);
    this.dispatchEvent(new CustomEvent('value-picked', { detail: { value } }));
    this.requestUpdate();
  }
}
